import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Autocomplete, debounce, Grid, IconButton, InputAdornment, InputLabel, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from '@material-ui/icons/Search';
import { RedTheme } from "common/global";
import { DATE, NUMBER_ARRAY } from "common/global/constants";
import { DateRangePicker, UserDetail } from "common/components";
import { useScreen } from "common/hooks/useScreen";
import { DelegateSearchedUserList } from "TAP/redux/actions/delegateUserAction";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as CrossIcon } from 'common/assets/images/CrossIcon.svg';
import dayjs from "dayjs";
import { fontWeightStyles } from "TAP/styles/genericStyles/fontWeights";
import { isLocalStorageKeyExistDefined } from "TAP/utils/commonMethods";

export const DelegatePopUp = ({saveCallBack, isViewMode, logInUserDetails, isEditMode}) => {
    const [allUserList, setAllUserList] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [viewDelgateData, setViewDelegateData] = useState('');
    const theme = RedTheme;
    const screen = useScreen();
    const dispatch = useDispatch();

    const searchedUserListRdState = useSelector((state) => state?.delegateUserRd?.searchedUserList);
    const isLoadingSearchedUserList = useSelector((state) => state?.delegateUserRd?.isLoadingSearchedUserList);
    const delegateUserView = useSelector((state) => state?.delegateUserRd?.delegateUserView[NUMBER_ARRAY.zero]); 
    const isLoadingViewDelegate = useSelector((state) => state?.delegateUserRd?.isLoadingViewDelegate);

    const inputLabelStyle = {
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: '"JohnsonText-Regular"',
        height: screen.isTablet ? '22px' : '16px',
        paddingTop: screen.isTablet ? '3px' : NUMBER_ARRAY.zero,
        lineHeight: 'unset'
    }

    const labelTypoGraphyStyle = {
        fontSize: '1.2rem',
        fontFamily: '"JohnsonText-inter"',
        fontStyle: 'normal',
        fontWeight: fontWeightStyles.fw5.fontWeight,
        color: theme.palette.text.lightCaption
    }
    const labelDataStyle = {    
        fontSize: '1.5rem',
        fontFamily: '"JohnsonText-inter"',
        fontWeight: fontWeightStyles.fw4.fontWeight,
        color: theme.palette.text.base
    }
    const errorHelperMessage = {    
        fontSize: '1.2rem',
        fontFamily: '"JohnsonText-inter"',
        fontWeight: fontWeightStyles.fw4.fontWeight,
        color: theme.palette.error.dark
    }

    const getLabeledOptions = (option) => {
        if (option && option !== '') {
            return `(${option.user_id}) ${option.full_name}`;
        } else {
            return ''
        }
    }

    const triggerItemAsyncChange = useMemo(
		() =>
			debounce((request) => {
				handleSearchWWIDsAsyncChange([...request.input]);
			}, NUMBER_ARRAY.fourHundred),
		[]
	);

    const handleSearchWWIDsAsyncChange = (newValue) => {
		const searchText = newValue.join('').toString();
		if (searchText.length >= NUMBER_ARRAY.one) {
			dispatch(DelegateSearchedUserList({
                "loggedInUserId": logInUserDetails?.user_id,
                "loggedInUserEmail": logInUserDetails?.user_email,
                "searchText": searchText
            }));
		} else {
			setAllUserList([]);
		}
	};

    const handleInputChange = (_event, value) => {
        setInputValue(value);
    }

    const handleOnChange = (_event, value) => {
        setSelectedUser(value);
    }

    const handleDelete = () => {
        setSelectedUser('');
        setAllUserList([]);
    }

    const getFormattedDate = (date) => {
        return dayjs(date).format('DD.MMM.YYYY');
    }

    const viewModeContent = () => {
        return (
            <Fragment>
                <Grid container>
                    <Grid item md={6} padding={'10px 0px 0px 10px'}>
                        <Typography sx={labelTypoGraphyStyle}>
                            {`WWID / User Name`}
                        </Typography>
                        {(viewDelgateData && viewDelgateData !== '') &&
                            (
                                <Stack display={'flex'} direction={'row'} gap={'30px'}>
                                    <Typography sx={labelDataStyle}>
                                        {viewDelgateData?.user_full_name || ''}
                                    </Typography>
                                    <Typography sx={{ ...labelDataStyle, color: theme.palette.text.dark }}>
                                        {viewDelgateData?.user_id|| ''}
                                    </Typography>
                                </Stack>
                            )
                        }
                        <Stack>
                            <Typography sx={labelTypoGraphyStyle}>
                                {`Period:`}
                            </Typography>
                            {(viewDelgateData && viewDelgateData !== '') && (
                                <Fragment>
                                    <Typography sx={labelDataStyle}>
                                        {`${getFormattedDate(viewDelgateData?.start_date)} - ${getFormattedDate(viewDelgateData?.end_date)}` || ''}
                                    </Typography>
                                    {(viewDelgateData?.STATUS === 'Expired') &&
                                        <Typography sx={errorHelperMessage}>
                                            {`The current assigned period is expired.`}
                                        </Typography>
                                    }
                                </Fragment>                       
                            )} 
                        </Stack>
                       
                    </Grid>
                </Grid>
            </Fragment>
        )
    }

    const autoCompleteSingleSelection = () => {
        return (
            <Fragment>
                <Autocomplete 
                    options={allUserList}
                    autoFocus={false}
                    getOptionLabel={getLabeledOptions}
                    loading= {isLoadingSearchedUserList}
                    value={selectedUser}
                    noOptionsText={'No match found'}
                    isOptionEqualToValue={(option, value) => option?.user_id === value?.user_id}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    onChange={handleOnChange}
                    renderInput={(params) => (
                        <TextField
                            className="delegate-auto-text"
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                shrink: "true",
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <Fragment>
                                        {!inputValue ? null : (
                                            <InputAdornment position="end">
                                                <IconButton onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDelete();
                                                    setInputValue('');
                                                }}>
                                                    <CrossIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    </Fragment>
                                )
                            }}
                            placeholder={'Search'}
                            label={
                                <Stack display={'flex'} flexDirection={'row'} gap={'10px'}>
                                    <Typography
                                        sx={{...inputLabelStyle,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {`${`WWID/User Name`}*`}
                                    </Typography>
                                </Stack>
                            }
                            sx={{
                                borderBottom: `1px solid ${theme.palette.fontColor.gray_orange}`,
                            }}
                            variant='standard'
                            margin='normal'
                        />
                    )}
                />

            </Fragment>
        )
    }

    const onDateRangeChange = (event, type) => {
        if (type === 'Date') {
            if (event?.length && event[NUMBER_ARRAY.zero]?.length) {
                const dateRangeParentFormatted = event ? event.map((date) => dayjs(date)) : null;
                const dateFormatted = dateRangeParentFormatted?.map((date) => date.format('YYYY-MM-DD')) ?? []
                setSelectedDateRange(dateFormatted);
                setSelectedDate(dateRangeParentFormatted);
            } else {
                setSelectedDate([]);
                event[NUMBER_ARRAY.zero] = null;
                event[NUMBER_ARRAY.zero] = null;
                setSelectedDateRange(event);
            }
        }
    }

    const createSavePayload = () => {
        let delegateObj = undefined;
        const payload = {
            "id": isEditMode ? viewDelgateData?.id : "",
            "user_id": selectedUser?.user_id || '',
            "user_email": selectedUser?.user_email || '',
            "user_full_name": selectedUser?.full_name || '',
            "manager_id": logInUserDetails?.user_id || '',
            "manager_email": logInUserDetails?.user_email || '',
            "start_date": selectedDateRange[0] || '',
            "end_date": selectedDateRange[1] || '',
            "requested_by": logInUserDetails?.user_id
        }
        if (isLocalStorageKeyExistDefined('delegateProfileSwitched') && localStorage.getItem('delegateProfileSwitched') !== '[object Object]') {
            delegateObj = JSON.parse(localStorage.getItem('delegateProfileSwitched'));
        }
        if (delegateObj && delegateObj.isDelegateSwitch) {
            payload.manager_email = UserDetail().userEmailId;
        }
        return payload;    
    }

    const requiredFieldNotFilled = () => {
        return ((selectedUser == '' || selectedUser == undefined || selectedUser == null) ||
                (selectedDate == null || selectedDate == undefined || selectedDate == ''
                || selectedDate.length == NUMBER_ARRAY.zero))
    }

    const dirtyCheckDateRange = () => {
        let date = [viewDelgateData?.start_date, viewDelgateData?.end_date];
        const dateParentFormatted = date.map((date) => dayjs(date));
        date = dateParentFormatted.map((date) => date.format('YYYY-MM-DD'));
        const selectedDateParentFormat = selectedDateRange.map((date) => dayjs(date));
        const selectedDateRangeFormatted = selectedDateParentFormat.map((date) => date.format('YYYY-MM-DD'));
        const dateChanged = selectedDateRangeFormatted.map((item) => {return date.includes(item)})
        return !dateChanged.includes(false);
    }

    const dirtyCheckForSave = () => {
        return viewDelgateData?.user_id === selectedUser?.user_id && dirtyCheckDateRange()
    }

    useEffect(() => {
        setSelectedDate('');
        saveCallBack({}, false, true);
    }, [])

    useEffect(() => {
        if(!isLoadingViewDelegate && delegateUserView){
            setViewDelegateData(delegateUserView)
        }
    }, [isLoadingViewDelegate, delegateUserView])

    useEffect(() => {
        if (viewDelgateData !== '') {
            const userObj = {
                "user_id": viewDelgateData?.user_id,
                "full_name": viewDelgateData?.user_full_name,
                "user_email": viewDelgateData?.user_email
            }
            setSelectedUser(userObj);
            setSelectedDateRange([viewDelgateData?.start_date, viewDelgateData?.end_date]);
            
        }
    }, [viewDelgateData])

    useEffect(() => {
        if(!isViewMode && (selectedUser !== '' || (selectedDate !== null && selectedDate !== ''))){
            const savePayload = createSavePayload();
            const requiredFieldValidation = requiredFieldNotFilled();
            const isSaveDisabled = dirtyCheckForSave();
            saveCallBack(savePayload, isSaveDisabled, requiredFieldValidation);
        }
    }, [selectedUser, selectedDate, isViewMode])

    useEffect(() => {
        if(inputValue !== ''){
            triggerItemAsyncChange({ input: inputValue });
        } else {
            setAllUserList([]);
        }

    }, [inputValue])

    useEffect(() => {
        if (searchedUserListRdState && searchedUserListRdState.length > NUMBER_ARRAY.zero){
            setAllUserList(searchedUserListRdState);
        }
    }, [searchedUserListRdState])

     useEffect(() => {
            if(selectedDateRange.length > NUMBER_ARRAY.one && !selectedDateRange.includes(null)){
                const dateRangeParentFormatted = selectedDateRange
                    ? selectedDateRange.map((date) => dayjs(date))
                    : null;
                setSelectedDate(dateRangeParentFormatted);
            }
        }, [selectedDateRange]);

    return (
        <Fragment>
            {!isViewMode ? (
                <Grid container>
                    <Grid item md={6} sm={6} xs={12}>
                        <Typography>
                            {autoCompleteSingleSelection()}
                        </Typography>
                    </Grid>
                    <Grid item md={6} sm={6} xs={12} sx={{ paddingLeft: !screen.isMobile ? '50px' : '0px' }}>
                        <Stack display={'flex'} marginTop={'15px'}>
                            <Stack direction={'column'}>
                                <Stack direction={'row'} gap={'10px'}>
                                    <InputLabel sx={{
                                        ...inputLabelStyle,
                                        color: theme.palette.text.primary,
                                        height: '18px !important'
                                    }}>
                                        {`Date Range*`}
                                    </InputLabel>
                                </Stack>
                                <Stack>
                                    <DateRangePicker
                                        height={'33px'}
                                        width={'100%'}
                                        onChange={(e) => onDateRangeChange(e, DATE)}
                                        marginTop={'0px'}
                                        padding={'4px, 0px'}
                                        value={selectedDate}
                                        isDisablePastRequire={true}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Grid>

                </Grid>
            ) : 
            (
                viewModeContent()
            )
            }
        </Fragment>
    );
} 
